import { useEffect, useState } from 'react'
import { apiFetch } from '@/lib/api'
import { useToast } from '@/components/ToastContainer'
import { Button, Spinner, Tabs } from '@/ui'
import { ReferencePhotoQualityHistory } from './ReferencePhotoQualityHistory'
import styles from './ReferencePhotoQualitySettings.module.css'

const SETTING_KEY = 'reference_photo_quality_thresholds'

export interface Band { warn: number; block: number }
export interface Brightness { warnLow: number; blockLow: number; warnHigh: number }
export interface Thresholds {
  confidenceMin: number
  yaw: Band
  pitch: Band
  roll: Band
  brightness: Brightness
  sharpnessMin: Band
  faceSizeMin: Band
}

const DEFAULTS: Thresholds = {
  confidenceMin: 90,
  yaw: { warn: 20, block: 35 },
  pitch: { warn: 20, block: 35 },
  roll: { warn: 20, block: 40 },
  brightness: { warnLow: 30, blockLow: 15, warnHigh: 90 },
  sharpnessMin: { warn: 40, block: 20 },
  faceSizeMin: { warn: 0.08, block: 0.03 },
}

interface AppSettingRow { key: string; value: unknown }

const TABS = [
  { key: 'thresholds', label: '임계값 설정' },
  { key: 'history', label: '검수 히스토리' },
]

export function ReferencePhotoQualitySettings() {
  const { addToast } = useToast()
  const [values, setValues] = useState<Thresholds>(DEFAULTS)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [tab, setTab] = useState<'thresholds' | 'history'>('thresholds')

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const rows = await apiFetch<AppSettingRow[]>('/api/admin/settings?category=AI')
        const row = rows.find((r) => r.key === SETTING_KEY)
        if (!cancelled && row && row.value && typeof row.value === 'object') {
          setValues({ ...DEFAULTS, ...(row.value as Partial<Thresholds>) })
        }
      } catch {
        // 없으면 기본값 유지
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const handleSave = async () => {
    setSaving(true)
    try {
      await apiFetch('/api/admin/settings', {
        method: 'PATCH',
        body: JSON.stringify({
          key: SETTING_KEY,
          value: values,
          category: 'AI',
          label: '참조사진 품질 임계값',
          description: '각도·조명·선명도 warn/block 경계',
        }),
      })
      addToast({ type: 'success', message: '저장되었습니다' })
    } catch (err: unknown) {
      addToast({ type: 'error', message: err instanceof Error ? err.message : '저장 실패' })
    } finally {
      setSaving(false)
    }
  }

  const bandRow = (
    label: string,
    key: 'yaw' | 'pitch' | 'roll' | 'sharpnessMin' | 'faceSizeMin',
    hint: string,
    step = 1,
  ) => (
    <div className={styles.row} key={key}>
      <div className={styles.rowMain}>
        <span className={styles.label}>{label}</span>
        <label>
          warn{' '}
          <input
            type="number"
            step={step}
            value={values[key].warn}
            onChange={(e) => setValues((v) => ({ ...v, [key]: { ...v[key], warn: Number(e.target.value) } }))}
          />
        </label>
        <label>
          block{' '}
          <input
            type="number"
            step={step}
            value={values[key].block}
            onChange={(e) => setValues((v) => ({ ...v, [key]: { ...v[key], block: Number(e.target.value) } }))}
          />
        </label>
      </div>
      <p className={styles.hint}>{hint}</p>
    </div>
  )

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner />
      </div>
    )
  }

  // 입력 포커스 유지를 위해 컴포넌트가 아닌 JSX 변수로 둔다 (탭 전환 시 리마운트 방지)
  const thresholdsForm = (
    <div className={styles.root}>
      <div className={styles.header}>
        <div>
          <p className={styles.subtitle}>
            block 기준을 넘으면 업로드 거부, warn 기준을 넘으면 경고 후 허용됩니다.
          </p>
        </div>
        <Button variant="secondary" onClick={() => setValues(DEFAULTS)} disabled={saving}>
          기본값 복원
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? '저장 중...' : '저장'}
        </Button>
      </div>

      <div className={styles.helpPanel}>
        <p className={styles.helpTitle}>판정 방식</p>
        <ul className={styles.helpList}>
          <li>
            <b>block</b> = 업로드 거부(400). 하나라도 block 기준을 넘으면 사진을 올릴 수 없습니다.
          </li>
          <li>
            <b>warn</b> = 경고만 표시하고 업로드는 허용. 사용자는 &ldquo;그대로 업로드&rdquo;로 진행할 수 있습니다.
          </li>
          <li>
            <b>ok</b> = 모든 지표가 통과. 경고 없이 업로드됩니다.
          </li>
        </ul>
        <p className={styles.helpNote}>
          block 값은 항상 warn 값보다 <b>더 극단(더 관대)</b>이어야 합니다. 예: 각도는 warn=20°/block=35°(20° 넘으면
          경고, 35° 넘으면 거부), 초점·얼굴크기는 &ldquo;그 값 <b>미만</b>&rdquo;일 때 걸리므로 warn=40/block=20처럼
          block이 더 작습니다.
        </p>
        <p className={styles.helpNote}>
          <b>참고</b> 얼굴이 0명이거나 2명 이상이면 이 임계값과 무관하게 항상 거부됩니다(&ldquo;1명의 얼굴만
          업로드해주세요&rdquo;). 임계값은 얼굴이 정확히 1명일 때만 적용됩니다.
        </p>
      </div>

      <div className={styles.fields}>
        <div className={styles.row}>
          <div className={styles.rowMain}>
            <span className={styles.label}>얼굴 확신도 하한 (block)</span>
            <label>
              <input
                type="number"
                value={values.confidenceMin}
                onChange={(e) => setValues((v) => ({ ...v, confidenceMin: Number(e.target.value) }))}
              />
            </label>
          </div>
          <p className={styles.hint}>
            Rekognition이 &ldquo;이건 얼굴이다&rdquo;라고 판단한 확신도(0~100). 이 값 <b>미만</b>이면 거부합니다.
            기본 90. 낮추면 옆모습·흐린 얼굴도 통과하지만 오검출 위험이 커집니다.
          </p>
        </div>

        {bandRow('좌우 각도 |yaw|', 'yaw', '얼굴이 좌우로 돌아간 정도(°). 절대값이 warn을 넘으면 경고, block을 넘으면 거부. 정면=0°. 기본 warn 20 / block 35.')}
        {bandRow('상하 각도 |pitch|', 'pitch', '얼굴이 위아래로 젖혀진 정도(°, 올려찍음·내려찍음). 절대값 기준. 기본 warn 20 / block 35.')}
        {bandRow('기울기 |roll|', 'roll', '얼굴이 좌우로 기울어진 정도(°, 고개 갸웃). 절대값 기준. 기본 warn 20 / block 40.')}

        <div className={styles.row}>
          <div className={styles.rowMain}>
            <span className={styles.label}>밝기 brightness</span>
            <label>
              warnLow{' '}
              <input
                type="number"
                value={values.brightness.warnLow}
                onChange={(e) => setValues((v) => ({ ...v, brightness: { ...v.brightness, warnLow: Number(e.target.value) } }))}
              />
            </label>
            <label>
              blockLow{' '}
              <input
                type="number"
                value={values.brightness.blockLow}
                onChange={(e) => setValues((v) => ({ ...v, brightness: { ...v.brightness, blockLow: Number(e.target.value) } }))}
              />
            </label>
            <label>
              warnHigh{' '}
              <input
                type="number"
                value={values.brightness.warnHigh}
                onChange={(e) => setValues((v) => ({ ...v, brightness: { ...v.brightness, warnHigh: Number(e.target.value) } }))}
              />
            </label>
          </div>
          <p className={styles.hint}>
            얼굴 영역 밝기(0~100). <b>blockLow 미만</b>이면 너무 어두워 거부, <b>warnLow 미만</b>이거나
            <b> warnHigh 초과</b>면 경고(어둡거나 과노출). 상한 거부는 없습니다. 기본 warnLow 30 / blockLow 15 / warnHigh 90.
          </p>
        </div>

        {bandRow('초점 sharpness', 'sharpnessMin', '얼굴 영역 선명도(0~100). 이 값 미만이면 걸립니다(초점이 나감). block(20)이 warn(40)보다 작아야 합니다. 기본 warn 40 / block 20.')}
        {bandRow('얼굴 크기 faceSize', 'faceSizeMin', '사진 전체 대비 얼굴이 차지하는 넓이 비율(0~1, 가로x세로). 이 값 미만이면 걸립니다(얼굴이 너무 작음/멀리 있음). 기본 warn 0.08 / block 0.03.', 0.01)}
      </div>
    </div>
  )

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <h1 className={styles.title}>참조사진 품질</h1>
        <p className={styles.subtitle}>
          AI 생성에 쓸 신랑·신부 참조사진을 업로드할 때 Rekognition으로 얼굴 품질을 검사합니다.
          임계값을 조정하고, 실제 검수 판정 히스토리로 그 근거를 확인하세요.
        </p>
      </div>

      <Tabs items={TABS} value={tab} onChange={(k) => setTab(k as 'thresholds' | 'history')} />

      {tab === 'history' ? <ReferencePhotoQualityHistory thresholds={values} /> : thresholdsForm}
    </div>
  )
}
